import { Dayjs } from 'dayjs'
import { SetOptional } from 'type-fest'
import { z } from 'zod'

import { day, getToday, isSameOrBefore } from '~/astra'
import { Schemas } from '~/clients/api-client'
import { type HospitalConfigPractitioner, Include, PractitionerProfile } from '~/storm'
import { getPractitionerFullname } from '~/utils/dips'

import { formatToIsoString, getActiveAndFutureProfiles } from '../../Form/utils'
import type { PractitionerFormInitialValues } from './PractitionerForm'

const createSurgeryTypeGroupError = ({ surgeryTypeGroupCode, message }: { message: string; surgeryTypeGroupCode: string }) => ({
    message,
    code: z.ZodIssueCode.custom,
    params: {
        code: surgeryTypeGroupCode,
    },
})

type SurgeryTypeGroupError = ReturnType<typeof createSurgeryTypeGroupError>
export const isSurgeryTypeGroupError = (error: FormError): error is SurgeryTypeGroupError => !!error && 'params' in error && error.params?.code !== undefined

export type FormError =
    | {
          message: string
          params?: Record<string, unknown>
      }
    | undefined

const servicePeriodSchema = z
    .object({
        id: z.coerce.number().optional(),
        startDate: z.custom<Dayjs>().optional(),
        endDate: z.custom<Dayjs>().optional(),
    })
    .superRefine((data, ctx) => {
        if (data.startDate && data.endDate && isSameOrBefore(data.endDate, data.startDate)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Startdato må være før slutt dato', path: ['startDate'] })
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Sluttdato må være etter startdato', path: ['endDate'] })
        }
    })
export type FormServicePeriod = z.infer<typeof servicePeriodSchema>

const profileSurgeryTypeGroupSchema = z.object({
    id: z.number().optional(),
    surgeryTypeGroup: z.object({
        id: z.number(),
        code: z.string(),
        displayName: z.string().optional(),
    }),
})

export type FormProfileSurgeryTypeGroup = z.infer<typeof profileSurgeryTypeGroupSchema>

const validateSurgeryTypeGroupDuplicates = (
    data: { mainSurgeryTypeGroups: FormProfileSurgeryTypeGroup[]; inTrainingSurgeryTypeGroups: FormProfileSurgeryTypeGroup[] },
    ctx: z.RefinementCtx
) => {
    data.mainSurgeryTypeGroups.forEach(s => {
        const isDuplicate = data.inTrainingSurgeryTypeGroups.some(t => t.surgeryTypeGroup.code === s.surgeryTypeGroup.code)
        if (isDuplicate) {
            ctx.addIssue({
                ...createSurgeryTypeGroupError({
                    surgeryTypeGroupCode: s.surgeryTypeGroup.code,
                    message: 'Kan ikke ha samme operasjonsgruppe i både hovedoperatør og assistent',
                }),
                path: ['mainSurgeryTypeGroups'],
            })
            ctx.addIssue({
                ...createSurgeryTypeGroupError({
                    surgeryTypeGroupCode: s.surgeryTypeGroup.code,
                    message: 'Kan ikke ha samme operasjonsgruppe i både hovedoperatør og assistent',
                }),
                path: ['inTrainingSurgeryTypeGroups'],
            })
        }
    })
}

type ExtraStaffingConfig = Schemas['PractitionerStaffingConfig']

export const DEFAULT_EXTRA_STAFFING_CONFIG = {
    num_ox: 0,
    work_gained_per_poli_day: 180,
    work_gained_per_evening_poli_day: 180,
    work_done_per_session: [360, 360, 360, 360, 360],
    exclude_from_too_few_surgeons_warning: false,
    poli_requirements: {},
    waiting_list: {},
}
/**
 * Expert-only solver config that can be overridden per profile (ExtraStaffingConfig).
 * The whole config is optional; when present each scalar field is validated.
 * `poli_requirements` is not edited in the UI but is preserved round-trip so
 * we don't clobber values set elsewhere.
 */
export const extraStaffingConfigSchema = z
    .object({
        num_ox: z.number({ invalid_type_error: 'Påkrevd felt' }).int('Må være et heltall').min(0, 'Kan ikke være negativ'),
        work_gained_per_poli_day: z.number({ invalid_type_error: 'Påkrevd felt' }).int('Må være et heltall'),
        work_gained_per_evening_poli_day: z.number({ invalid_type_error: 'Påkrevd felt' }).int('Må være et heltall'),
        work_done_per_session: z.array(z.number().int('Må være heltall').min(0, 'Kan ikke være negativ')),
        exclude_from_too_few_surgeons_warning: z.boolean(),
        poli_requirements: z.custom<ExtraStaffingConfig['poli_requirements']>().optional(),
        waiting_list: z.record(z.string(), z.number().int('Må være heltall').min(0, 'Kan ikke være negativ')).optional(),
    })
    .default(DEFAULT_EXTRA_STAFFING_CONFIG)

export type FormExtraStaffingConfig = z.infer<typeof extraStaffingConfigSchema>

const profileSchema = z
    .object({
        id: z.number().optional(),
        activeFrom: z.custom<Dayjs>().optional(),
        mainSurgeryTypeGroups: z.array(profileSurgeryTypeGroupSchema),
        inTrainingSurgeryTypeGroups: z.array(profileSurgeryTypeGroupSchema),
        extraStaffingConfig: extraStaffingConfigSchema.optional().nullable(),
    })
    .superRefine(validateSurgeryTypeGroupDuplicates)

export type FormPractitionerProfile = z.infer<typeof profileSchema>

export const practitionerFormSchema = z.object({
    shortName: z.string(),
    fullName: z.string().optional(),
    servicePeriods: z.array(servicePeriodSchema),
    specialityId: z.number({ required_error: 'Påkrevd felt' }),
    activeProfile: profileSchema,
    futureProfiles: z.array(profileSchema).superRefine((data, ctx) => {
        let previousActiveFrom: Dayjs | undefined
        data.forEach((p, index) => {
            if (!p.activeFrom) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Påkrevd felt', path: [index, 'activeFrom'] })
                return
            }
            if (isSameOrBefore(p.activeFrom, getToday())) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Aktiv fra må være etter i dag', path: [index, 'activeFrom'] })
            }
            if (previousActiveFrom && isSameOrBefore(p.activeFrom, previousActiveFrom)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Startdato må være etter at forrige periode begynner', path: [index, 'activeFrom'] })
            }
            previousActiveFrom = p.activeFrom
        })
    }),
})
export type PractitionerFormParsedValues = z.infer<typeof practitionerFormSchema>

export type PractitionerFormDefaultValues = SetOptional<PractitionerFormParsedValues, 'specialityId'>

export const defaultFormValues: PractitionerFormDefaultValues = {
    shortName: '',
    fullName: '',
    // require selecting a speciality if empty
    specialityId: undefined,
    servicePeriods: [],
    activeProfile: {
        id: undefined,
        activeFrom: undefined,
        mainSurgeryTypeGroups: [],
        inTrainingSurgeryTypeGroups: [],
        extraStaffingConfig: undefined,
    },
    futureProfiles: [],
}

const toFormProfile = (profile: Include<PractitionerProfile, { profileSurgeryTypeGroups: { surgeryTypeGroup: true } }>): FormPractitionerProfile => ({
    id: profile.id,
    activeFrom: profile.active_from ? day(profile.active_from) : undefined,
    mainSurgeryTypeGroups: profile.profileSurgeryTypeGroups.flatMap(s =>
        s.surgeryTypeGroup && s.status === 'main' ? [{ id: s.id, surgeryTypeGroup: s.surgeryTypeGroup }] : []
    ),
    inTrainingSurgeryTypeGroups: profile.profileSurgeryTypeGroups.flatMap(s =>
        s.surgeryTypeGroup && s.status === 'in_training' ? [{ id: s.id, surgeryTypeGroup: s.surgeryTypeGroup }] : []
    ),
    extraStaffingConfig: profile.extra_staffing_config ?? undefined,
})

export function practitionerToFormState(practitioner: HospitalConfigPractitioner) {
    const formProfiles = practitioner.practitionerProfiles.map(toFormProfile)
    const { active: activeFormProfile, future: futureFormProfiles } = getActiveAndFutureProfiles(formProfiles)

    return {
        shortName: practitioner.short_name,
        fullName: getPractitionerFullname(practitioner),
        servicePeriods: practitioner.servicePeriods.map(({ id, start_time, end_time }) => ({
            id,
            startDate: start_time ? day(start_time) : undefined,
            endDate: end_time ? day(end_time) : undefined,
        })),
        activeProfile: activeFormProfile ?? defaultFormValues.activeProfile,
        specialityId: practitioner.speciality_id,
        futureProfiles: futureFormProfiles,
    } satisfies PractitionerFormInitialValues
}

export type ProfileWithSurgeryTypeGroups = Schemas['PractitionerProfileCreate'] & {
    id?: number
    profileSurgeryTypeGroups: { id?: number; surgery_type_group_id: number; status: 'main' | 'in_training' }[]
}

export function formStateToApiPayloads(formState: PractitionerFormDefaultValues, practitionerId: number) {
    // we group these because there are separate CRUD endpoints
    const practitionerProfiles: ProfileWithSurgeryTypeGroups[] = [formState.activeProfile, ...formState.futureProfiles]
        .filter(Boolean)
        .map(profile => {
            const mainGroups = profile.mainSurgeryTypeGroups.map(s => ({
                surgery_type_group_id: s.surgeryTypeGroup.id,
                id: s.id,
                status: 'main' as const,
            }))
            const inTrainingGroups = profile.inTrainingSurgeryTypeGroups.map(s => ({
                surgery_type_group_id: s.surgeryTypeGroup.id,
                status: 'in_training' as const,
                id: s.id,
            }))
            return {
                practitioner_id: practitionerId,
                id: profile.id,
                active_from: formatToIsoString(profile.activeFrom),
                extra_staffing_config: profile.extraStaffingConfig,
                profileSurgeryTypeGroups: [...mainGroups, ...inTrainingGroups],
            } satisfies ProfileWithSurgeryTypeGroups
        })
        // do not bother sending empty profiles (eg. new profiles with no surgery groups or no active from)
        .filter(p => p.id !== undefined || p.profileSurgeryTypeGroups.length > 0 || !!p.active_from || p.extra_staffing_config !== undefined)

    return {
        practitioner: {
            short_name: formState.shortName,
            speciality_id: formState.specialityId,
            role: 'surgeon',
        } satisfies Schemas['PractitionerCreate'],
        practitionerProfiles: practitionerProfiles,
        servicePeriods: formState.servicePeriods.map(p => ({
            practitioner_id: practitionerId,
            id: p.id,
            start_time: formatToIsoString(p.startDate),
            end_time: formatToIsoString(p.endDate),
        })),
    }
}
